import React from 'react'

import type { ApplicantSkill, CompanyInterview } from '../../../api/companies/types'
import { useI18N } from '../../../libraries/i18n/hook'
import { InnerTabGroup } from '../form/tab-groups'
import CvIcon from '../../../assets/icons/cv.svg?react'
import ProcessIcon from '../../../assets/icons/selection-process-icon.svg?react'
import CheckedIcon from '../../../assets/icons/checked.svg'
import UncheckedIcon from '../../../assets/icons/unchecked.svg'
import Tag from '../content/Tag'
import SanitizeHTML from '../layout/SanitizeHtml'
import IconTitleSection from '../layout/IconTitleSection'
import ExternalLink from '../layout/ExternalLink'
import TagGroup from '../content/TagGroup'

type Props = { interview: CompanyInterview, allSkills: Array<ApplicantSkill>, languageCode?: string }
const TABS: {[x: string]: string } = {
  GET_INTERVIEW: 'GET_INTERVIEW',
  PASS_INTERVIEW: 'PASS_INTERVIEW',
}
const InterviewCard:React.FC<Props> = ({ interview, allSkills, languageCode }) => {
  const { t } = useI18N()
  const [activeTab, setActiveTab] = React.useState(TABS.GET_INTERVIEW)
  const tabs = []
  tabs.push({ id: TABS.GET_INTERVIEW, title: t('companies.interview.get', undefined, languageCode) })
  if(interview.process) {
    tabs.push({ id: TABS.PASS_INTERVIEW, title: t('companies.interview.pass', undefined, languageCode) })
  }

  const skillsAdded = interview.process?.applicantSkills?.map((skill) => skill.id)
  const skillsNotAdded = allSkills.filter(skill => !skillsAdded?.includes(skill.id))
  return (
    <div>
      <InnerTabGroup
        tabs={tabs}
        handleClick={(tabId: string) => {
          setActiveTab(tabId)
        }}
        active={activeTab}
        size='md'
      />
      {activeTab === TABS.GET_INTERVIEW && (
        <div>
          <IconTitleSection title={t('companies.interview.take.account', undefined, languageCode)} icon={<CvIcon width='inherit' />}>
            <div className='md:flex justify-between text-black gap-x-4'>
              <div className='md:max-w-[65%]'>
                <div><strong className='text-xs md:text-sm'>{t('companies.interview.doc.types', undefined, languageCode)}</strong></div>
                <div className='-ml-2 mt-3'>
                  <TagGroup>
                    {interview.documentTypes.map((doc) => (
                      <Tag outlined solid key={doc.label}>{doc.label}</Tag>
                    ))}
                  </TagGroup>
                </div>
                <div className='small-paragraph mt-2'>
                  <SanitizeHTML html={interview.requirementsInfo} />
                </div>
              </div>
            <div className='bg-soft-green rounded-lg p-5 small-paragraph mt-4 md:mt-0 md:w-1/2 md:max-w-[35%]'>
              <p className='font-semibold text:2xs md:text-xs'>{t('companies.interview.cv.valoration', undefined, languageCode)}</p>
              <p>{interview.cvValorations?.description}</p>
              {interview.cvValorations?.items.map((item) => {
                let widthUnfilled = 'w-0'
                switch(item.value) {
                  case 1:
                    widthUnfilled = 'w-[20%]'
                    break
                  case 2:
                    widthUnfilled = 'w-[40%]'
                    break
                  case 3:
                    widthUnfilled = 'w-[60%]'
                    break
                  case 4:
                    widthUnfilled = 'w-[80%]'
                    break
                  case 5:
                    widthUnfilled = 'w-full'
                    break
                }
                return (
                  <div key={item.label} className='md:grid grid-cols-6 gap-1 mt-3 md:mt-2'>
                    <div className='col-span-3 text-2xs md:text-xs font-normal'>{item.label}</div>
                    <div className={'col-span-3 bg-soft-green rounded-full h-4 mt-1'}>
                      {item.value !== 0 && (<p className={`text-end bg-deep-green block h-4 rounded-full m-0 ${widthUnfilled} text-white pe-3`}>
                        <small className='block text-2xs md:text-xs leading-none pt-0.5'>{item.value}</small>
                      </p>)}
                    </div>
                  </div>
                )})}
              </div>
            </div>
          </IconTitleSection>
        </div>
      )}
      {activeTab === TABS.PASS_INTERVIEW && (
        <div>
          <IconTitleSection title={t('companies.interview.process.works', undefined, languageCode)} icon={<ProcessIcon width='inherit' />}>
            <div className='md:flex items-start text-black'>
              <div className='md:w-3/4'>
                <div className='small-paragraph md:w-3/4'>
                  <SanitizeHTML html={interview.process.description} />
                </div>

                <div className='mt-4 small-paragraph'>
                  <p className='mb-3'><b>{t('companies.interview.skills.tested', undefined, languageCode)}</b></p>
                  <TagGroup>
                    {interview.process.applicantSkills?.map((skill) => (
                      <Tag iconSrc={CheckedIcon} iconClass='stroke-deep-green' outlined solid key={skill.label}>{skill.label}</Tag>
                    ))}
                  </TagGroup>

                  <TagGroup>
                    {skillsNotAdded.map((skill) => (
                      <div key={skill.label} className='grayscale inline-block'>
                        <Tag iconSrc={UncheckedIcon} iconClass='stroke-gray-200' outlined solid>{skill.label}</Tag>
                      </div>
                    ))}
                  </TagGroup>
                </div>

                {interview.process.moreInfo && (
                  <div className='mt-4 small-paragraph'>
                    <SanitizeHTML html={interview.process.moreInfo} />
                  </div>
                )}
              </div>

              {interview.process?.externalLinks && (
                <div className='mt-6 md:mt-0 md:ml-4 md:w-1/3 small-paragraph bg-soft-green rounded-lg p-5'>
                  <strong>{t('companies.interview.resources.links', undefined, languageCode)}</strong>
                  {interview.process.externalLinks.map((link) => (
                    <div key={link.label} className='mt-4 text-xs font-normal'>
                      <ExternalLink url={link.url}>
                        {link.label}
                      </ExternalLink>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </IconTitleSection>
        </div>
      )}
    </div>
  )
}

export default InterviewCard
